import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { offersAPI, checkoutAPI } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Check, X, RefreshCw, ShoppingBag, Paperclip } from "lucide-react";

const STATUS_COLORS = {
  pending: "border-amber-500/40 bg-amber-500/10 text-amber-400",
  accepted: "border-emerald-500/40 bg-emerald-500/10 text-emerald-400",
  declined: "border-red-500/40 bg-red-500/10 text-red-400",
  countered: "border-slate-500/40 bg-slate-500/10 text-slate-300",
  withdrawn: "border-slate-500/40 bg-slate-500/10 text-slate-300",
};

export default function OfferMessageCard({ offer, transaction, currentUserId, mine, onChanged }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [counterOpen, setCounterOpen] = useState(false);
  const [counterPrice, setCounterPrice] = useState("");
  const [counterShipping, setCounterShipping] = useState("");
  const [counterMsg, setCounterMsg] = useState("");

  const isQuote = !!offer.is_quote;
  const isRecipient = offer.recipient_id === currentUserId;
  const isBuyer = (offer.sender_role === "buyer" && offer.sender_id === currentUserId)
                  || (offer.sender_role === "seller" && offer.recipient_id === currentUserId);
  const status = offer.status || "pending";
  const total = Number(offer.total_amount ?? (Number(offer.item_price || 0) + Number(offer.shipping_cost || 0)));
  const showResponseButtons = status === "pending" && isRecipient;
  // Quotes are paid via the standard checkout on accept; products use the transaction buy-now.
  const showBuyNow = !isQuote && status === "accepted" && isBuyer && transaction && transaction.status !== "paid" && transaction.status !== "completed";

  const respond = async (action) => {
    setBusy(true);
    try {
      await offersAPI.respond(offer.id, { action });
      if (isQuote && action === "accept") {
        navigate(`/checkout/${offer.listing_id}?pickup=true`);
        return;
      }
      toast.success(action === "accept" ? "Accepted" : "Declined");
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not respond");
    } finally {
      setBusy(false);
    }
  };

  const submitCounter = async () => {
    const p = Number(counterPrice);
    if (!p || p <= 0) { toast.error("Enter a counter price"); return; }
    setBusy(true);
    try {
      await offersAPI.respond(offer.id, {
        action: "counter",
        counter_item_price: p,
        counter_shipping_cost: counterShipping ? Number(counterShipping) : undefined,
        counter_message: counterMsg || undefined,
      });
      setCounterOpen(false);
      setCounterPrice(""); setCounterShipping(""); setCounterMsg("");
      toast.success("Counter offer sent");
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send counter");
    } finally {
      setBusy(false);
    }
  };

  const buyNow = async () => {
    if (!transaction) return;
    setBusy(true);
    try {
      const r = await checkoutAPI.createSession("purchase", transaction.id);
      const url = r.data?.checkout_url;
      if (!url) throw new Error("Missing checkout url");
      window.location.assign(url);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start checkout");
      setBusy(false);
    }
  };

  return (
    <>
      <div className={`w-full max-w-md rounded-2xl border p-4 text-sm ${mine ? "border-primary/40 bg-primary/[0.06]" : "border-border bg-secondary/70"}`} data-testid={`offer-card-${offer.id}`}>
        <div className="mb-2 flex items-center justify-between gap-2">
          <span className="font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-muted-foreground">
            {isQuote ? "// QUOTE" : offer.sender_role === "seller" ? "// SELLER OFFER" : "// BUYER OFFER"}
          </span>
          <Badge className={`border text-[10px] ${STATUS_COLORS[status] || STATUS_COLORS.pending}`}>
            {status.toUpperCase()}
          </Badge>
        </div>
        <div className="rounded-lg border border-border/60 bg-background/40 p-3">
          <p className="truncate text-sm font-medium">{offer.listing_title || "Item"}</p>
          <div className="mt-2 flex items-baseline justify-between gap-3 text-xs text-muted-foreground">
            <span>{isQuote ? "Quoted price" : "Item price"}</span>
            <span className="font-medium text-foreground">${Number(offer.item_price || 0).toFixed(2)}</span>
          </div>
          {!isQuote && (
            <div className="mt-1 flex items-baseline justify-between gap-3 text-xs text-muted-foreground">
              <span>Shipping</span>
              <span className="font-medium text-foreground">${Number(offer.shipping_cost || 0).toFixed(2)}</span>
            </div>
          )}
          <div className="mt-2 flex items-baseline justify-between border-t border-border/60 pt-2">
            <span className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Total</span>
            <span className="font-display text-xl font-bold text-primary">${total.toFixed(2)}</span>
          </div>
        </div>
        {offer.message && (
          <p className="mt-2 text-xs italic text-muted-foreground">&ldquo;{offer.message}&rdquo;</p>
        )}
        {offer.attachment_url && (
          <a href={offer.attachment_url} target="_blank" rel="noreferrer" download={offer.attachment_name || "attachment"}
             className="mt-2 flex items-center gap-2 rounded-md border border-border/60 bg-background/40 p-2 text-xs font-medium text-primary hover:underline" data-testid={`offer-attachment-${offer.id}`}>
            <Paperclip className="h-3.5 w-3.5" /> {offer.attachment_name || "Attachment"}
          </a>
        )}
        {showResponseButtons && (
          isQuote ? (
            <div className="mt-3 grid grid-cols-2 gap-2">
              <Button size="sm" variant="outline" onClick={() => respond("decline")} disabled={busy} data-testid={`offer-decline-${offer.id}`}>
                <X className="mr-1 h-3.5 w-3.5" /> Decline
              </Button>
              <Button size="sm" onClick={() => respond("accept")} disabled={busy} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid={`offer-accept-${offer.id}`}>
                {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><ShoppingBag className="mr-1 h-3.5 w-3.5" /> Accept &amp; pay</>}
              </Button>
            </div>
          ) : (
          <div className="mt-3 grid grid-cols-3 gap-2">
            <Button size="sm" variant="outline" onClick={() => respond("decline")} disabled={busy} data-testid={`offer-decline-${offer.id}`}>
              <X className="mr-1 h-3.5 w-3.5" /> Decline
            </Button>
            <Button size="sm" variant="outline" onClick={() => setCounterOpen(true)} disabled={busy} data-testid={`offer-counter-${offer.id}`}>
              <RefreshCw className="mr-1 h-3.5 w-3.5" /> Counter
            </Button>
            <Button size="sm" onClick={() => respond("accept")} disabled={busy} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid={`offer-accept-${offer.id}`}>
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Check className="mr-1 h-3.5 w-3.5" /> Accept</>}
            </Button>
          </div>
          )
        )}
        {showBuyNow && (
          <div className="mt-3">
            <Button size="sm" onClick={buyNow} disabled={busy} className="w-full bg-primary text-primary-foreground hover:bg-primary/90" data-testid={`offer-buy-now-${offer.id}`}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><ShoppingBag className="mr-1 h-4 w-4" /> Buy now — ${total.toFixed(2)}</>}
            </Button>
          </div>
        )}
        {status === "accepted" && !showBuyNow && transaction?.status === "paid" && (
          <p className="mt-3 rounded-md border border-emerald-500/30 bg-emerald-500/10 p-2 text-center text-[11px] font-semibold uppercase tracking-widest text-emerald-400">
            Paid
          </p>
        )}
      </div>

      <Dialog open={counterOpen} onOpenChange={setCounterOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Send counter offer</DialogTitle>
            <DialogDescription>Propose different terms. The other party can accept, decline, or counter again.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="counter-price">Item price (USD)</Label>
              <Input id="counter-price" type="number" step="0.01" min="0" value={counterPrice} onChange={(e) => setCounterPrice(e.target.value)} placeholder={String(offer.item_price)} data-testid="counter-price-input" />
            </div>
            <div>
              <Label htmlFor="counter-shipping">Shipping (USD)</Label>
              <Input id="counter-shipping" type="number" step="0.01" min="0" value={counterShipping} onChange={(e) => setCounterShipping(e.target.value)} placeholder={String(offer.shipping_cost || 0)} data-testid="counter-shipping-input" />
            </div>
            <div>
              <Label htmlFor="counter-msg">Message (optional)</Label>
              <Input id="counter-msg" value={counterMsg} onChange={(e) => setCounterMsg(e.target.value)} data-testid="counter-message-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCounterOpen(false)} disabled={busy}>Cancel</Button>
            <Button onClick={submitCounter} disabled={busy} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="counter-submit">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send counter"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
